/*
 *
 *
 *
 *   Copyright (c) 2022. TinyMedic Pvt Ltd  - All Rights Reserved.
 *   This file is part of the API module  of the project: TinyMedic-Platform.
 *
 *
 *   Proprietary and confidential.
 *
 *   Unauthorized copying of this file, via any medium is strictly prohibited.
 *    Any resources of   TinyMedic-Platform can not be copied and/or distributed without the express permission of TinyMedic Pvt Ltd.
 *
 *
 *
 */

import React, { useEffect } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { Button, Col, FormText, Row } from 'reactstrap';
import { Translate, ValidatedField, ValidatedForm, isNumber, translate } from 'react-jhipster';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

import { convertDateTimeFromServer, convertDateTimeToServer, displayDefaultDateTime } from 'app/shared/util/date-utils';
import { useAppDispatch, useAppSelector } from 'app/config/store';

import { getEntities as getSurveyHeaders } from 'app/entities/survey-headers/survey-headers.reducer';
import { getEntities as getGlucoUsers } from 'app/entities/gluco-user/gluco-user.reducer';
import { CompletedByType } from 'app/shared/model/enumerations/completed-by-type.model';
import { createEntity, getEntity, reset, updateEntity } from './patient-survey.reducer';

export const PatientSurveyUpdate = () => {
  const dispatch = useAppDispatch();

  const navigate = useNavigate();

  const { id } = useParams<'id'>();
  const isNew = id === undefined;

  const surveyHeaders = useAppSelector(state => state.surveyHeaders.entities);
  const glucoUsers = useAppSelector(state => state.glucoUser.entities);
  const patientSurveyEntity = useAppSelector(state => state.patientSurvey.entity);
  const loading = useAppSelector(state => state.patientSurvey.loading);
  const updating = useAppSelector(state => state.patientSurvey.updating);
  const updateSuccess = useAppSelector(state => state.patientSurvey.updateSuccess);
  const completedByTypeValues = Object.keys(CompletedByType);

  const handleClose = () => {
    navigate(`/patient-survey${location.search}`);
  };

  useEffect(() => {
    if (isNew) {
      dispatch(reset());
    } else {
      dispatch(getEntity(id));
    }

    dispatch(getSurveyHeaders({}));
    dispatch(getGlucoUsers({}));
  }, []);

  useEffect(() => {
    if (updateSuccess) {
      handleClose();
    }
  }, [updateSuccess]);

  const saveEntity = values => {
    values.completedOn = convertDateTimeToServer(values.completedOn);
    if (values.cursor !== undefined && typeof values.cursor !== 'number') {
      values.cursor = Number(values.cursor);
    }
    if (values.lastupdated !== undefined && typeof values.lastupdated !== 'number') {
      values.lastupdated = Number(values.lastupdated);
    }
    if (values.microSec !== undefined && typeof values.microSec !== 'number') {
      values.microSec = Number(values.microSec);
    }
    if (values.timestampentry !== undefined && typeof values.timestampentry !== 'number') {
      values.timestampentry = Number(values.timestampentry);
    }

    const entity = {
      ...patientSurveyEntity,
      ...values,
      survey: surveyHeaders.find(it => it.id.toString() === values.survey?.toString()),
      patient: glucoUsers.find(it => it.id.toString() === values.patient?.toString()),
    };

    if (isNew) {
      dispatch(createEntity(entity));
    } else {
      dispatch(updateEntity(entity));
    }
  };

  const defaultValues = () =>
    isNew
      ? {
          completedOn: displayDefaultDateTime(),
        }
      : {
          completedByType: 'DOCTOR',
          ...patientSurveyEntity,
          completedOn: convertDateTimeFromServer(patientSurveyEntity.completedOn),
          survey: patientSurveyEntity?.survey?.id,
          patient: patientSurveyEntity?.patient?.id,
        };

  return (
    <div>
      <Row className="justify-content-center">
        <Col md="8">
          <h2 id="tinyMedicRestApp.patientSurvey.home.createOrEditLabel" data-cy="PatientSurveyCreateUpdateHeading">
            <Translate contentKey="tinyMedicRestApp.patientSurvey.home.createOrEditLabel">Create or edit a PatientSurvey</Translate>
          </h2>
        </Col>
      </Row>
      <Row className="justify-content-center">
        <Col md="8">
          {loading ? (
            <p>Loading...</p>
          ) : (
            <ValidatedForm defaultValues={defaultValues()} onSubmit={saveEntity}>
              {!isNew ? (
                <ValidatedField
                  name="id"
                  required
                  readOnly
                  id="patient-survey-id"
                  label={translate('tinyMedicRestApp.patientSurvey.id')}
                  validate={{ required: true }}
                />
              ) : null}
              <ValidatedField
                label={translate('tinyMedicRestApp.patientSurvey.completedOn')}
                id="patient-survey-completedOn"
                name="completedOn"
                data-cy="completedOn"
                type="datetime-local"
                placeholder="YYYY-MM-DD HH:mm"
              />
              <ValidatedField
                label={translate('tinyMedicRestApp.patientSurvey.completedByType')}
                id="patient-survey-completedByType"
                name="completedByType"
                data-cy="completedByType"
                type="select"
              >
                {completedByTypeValues.map(completedByType => (
                  <option value={completedByType} key={completedByType}>
                    {translate(`tinyMedicRestApp.CompletedByType.${completedByType}`)}
                  </option>
                ))}
              </ValidatedField>
              <ValidatedField
                label={translate('tinyMedicRestApp.patientSurvey.completedById')}
                id="patient-survey-completedById"
                name="completedById"
                data-cy="completedById"
                type="text"
              />
              <ValidatedField
                label={translate('tinyMedicRestApp.patientSurvey.active')}
                id="patient-survey-active"
                name="active"
                data-cy="active"
                check
                type="checkbox"
              />
              <ValidatedField
                label={translate('tinyMedicRestApp.patientSurvey.cursor')}
                id="patient-survey-cursor"
                name="cursor"
                data-cy="cursor"
                type="text"
                validate={{
                  required: { value: true, message: translate('entity.validation.required') },
                  validate: v => isNumber(v) || translate('entity.validation.number'),
                }}
              />
              <ValidatedField
                label={translate('tinyMedicRestApp.patientSurvey.lastupdated')}
                id="patient-survey-lastupdated"
                name="lastupdated"
                data-cy="lastupdated"
                type="text"
                validate={{
                  required: { value: true, message: translate('entity.validation.required') },
                  validate: v => isNumber(v) || translate('entity.validation.number'),
                }}
              />
              <ValidatedField
                label={translate('tinyMedicRestApp.patientSurvey.microSec')}
                id="patient-survey-microSec"
                name="microSec"
                data-cy="microSec"
                type="text"
                validate={{
                  required: { value: true, message: translate('entity.validation.required') },
                  validate: v => isNumber(v) || translate('entity.validation.number'),
                }}
              />
              <ValidatedField
                label={translate('tinyMedicRestApp.patientSurvey.timestampentry')}
                id="patient-survey-timestampentry"
                name="timestampentry"
                data-cy="timestampentry"
                type="text"
                validate={{
                  required: { value: true, message: translate('entity.validation.required') },
                  validate: v => isNumber(v) || translate('entity.validation.number'),
                }}
              />
              <ValidatedField
                id="patient-survey-survey"
                name="survey"
                data-cy="survey"
                label={translate('tinyMedicRestApp.patientSurvey.survey')}
                type="select"
                required
              >
                <option value="" key="0" />
                {surveyHeaders
                  ? surveyHeaders.map(otherEntity => (
                      <option value={otherEntity.id} key={otherEntity.id}>
                        {otherEntity.id}
                      </option>
                    ))
                  : null}
              </ValidatedField>
              <FormText>
                <Translate contentKey="entity.validation.required">This field is required.</Translate>
              </FormText>
              <ValidatedField
                id="patient-survey-patient"
                name="patient"
                data-cy="patient"
                label={translate('tinyMedicRestApp.patientSurvey.patient')}
                type="select"
                required
              >
                <option value="" key="0" />
                {glucoUsers
                  ? glucoUsers.map(otherEntity => (
                      <option value={otherEntity.id} key={otherEntity.id}>
                        {otherEntity.id}
                      </option>
                    ))
                  : null}
              </ValidatedField>
              <FormText>
                <Translate contentKey="entity.validation.required">This field is required.</Translate>
              </FormText>
              <Button tag={Link} id="cancel-save" data-cy="entityCreateCancelButton" to="/patient-survey" replace color="info">
                <FontAwesomeIcon icon="arrow-left" />
                &nbsp;
                <span className="d-none d-md-inline">
                  <Translate contentKey="entity.action.back">Back</Translate>
                </span>
              </Button>
              &nbsp;
              <Button color="primary" id="save-entity" data-cy="entityCreateSaveButton" type="submit" disabled={updating}>
                <FontAwesomeIcon icon="save" />
                &nbsp;
                <Translate contentKey="entity.action.save">Save</Translate>
              </Button>
            </ValidatedForm>
          )}
        </Col>
      </Row>
    </div>
  );
};

export default PatientSurveyUpdate;
